import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Users, ClipboardCheck, AlertTriangle, PackageCheck, RefreshCw, Search, Eye, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { StatusBadge } from '../components/guias/GuiaViewer';
import { ProfissionalPanel } from '../components/guias/ProfissionalPanel';
import { STATUS_STYLE, errMsg } from '../components/guias/guiasUtils';

const KPI = ({ icon: Icon, label, value, cls = '' }) => (
  <Card><CardContent className="p-3 flex items-center gap-3">
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${cls || 'bg-primary/10 text-primary'}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-xl font-bold" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value ?? '-'}</p></div>
  </CardContent></Card>
);

const ProfCard = ({ p, onOpen }) => (
  <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onOpen(p.key)} data-testid={`prof-card-${p.key}`}>
    <CardContent className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold truncate">{p.nome}</p>
          <p className="text-xs text-muted-foreground truncate">{p.categoria_label}{p.subtitulo ? ` · ${p.subtitulo}` : ''}</p>
        </div>
        <StatusBadge status={p.status} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <div className="rounded bg-muted/60 p-2"><p className="text-[10px] text-muted-foreground">{p.multi ? 'Pacientes' : 'Pac. competência'}</p><p className="font-bold">{p.pacientes_total ?? p.esperadas}</p></div>
        <div className="rounded bg-muted/60 p-2"><p className="text-[10px] text-muted-foreground">Guias</p><p className="font-bold">{p.multi ? (p.esperadas_definida ? `${p.validas}/${p.esperadas}` : p.validas) : `${p.validas}/${p.esperadas}`}</p></div>
        <div className="rounded bg-muted/60 p-2"><p className="text-[10px] text-muted-foreground">{p.multi ? 'Sem guia' : 'Pendentes'}</p><p className={`font-bold ${(p.multi ? p.pacientes_sem_guia : p.pendentes) > 0 ? 'text-red-600' : 'text-green-600'}`}>{p.multi ? p.pacientes_sem_guia : p.pendentes}</p></div>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${STATUS_STYLE[p.status]?.dot || 'bg-primary'} transition-all`} style={{ width: `${p.multi ? (p.pacientes_total ? Math.min(100, ((p.pacientes_total - p.pacientes_sem_guia) / p.pacientes_total) * 100) : 0) : (p.esperadas ? Math.min(100, (p.validas / p.esperadas) * 100) : 0)}%` }} />
      </div>
      <Button size="sm" variant="outline" className="w-full" data-testid={`btn-abrir-prof-${p.key}`}>
        {p.status === 'pronto_para_entrega' ? <CheckCircle2 className="h-4 w-4 mr-1 text-green-600" /> : <Eye className="h-4 w-4 mr-1" />}
        {p.status === 'pronto_para_entrega' ? 'Visualizar e confirmar' : 'Visualizar'}
      </Button>
    </CardContent>
  </Card>
);

export default function Guias() {
  const { api } = useAuth();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [competencia, setCompetencia] = useState(params.get('competencia') || new Date().toISOString().slice(0, 10));
  const [categoria, setCategoria] = useState('todas');
  const [status, setStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [ocultarVazios, setOcultarVazios] = useState(true);
  const selected = params.get('prof');

  useEffect(() => {
    api.get('/guias/meta').then((r) => {
      setMeta(r.data);
      const hoje = params.get('competencia') || r.data.hoje;
      if (!params.get('competencia')) setCompetencia(r.data.hoje);
      // Profissional (não recebedor) com uma única rota: abre direto seus pacientes
      if (!r.data.is_recebedor && r.data.meus_profissionais?.length === 1 && !params.get('prof')) {
        setParams({ prof: r.data.meus_profissionais[0], competencia: hoje });
      }
    });
  }, [api]); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = { competencia };
      if (categoria !== 'todas') q.categoria = categoria;
      if (status !== 'todos') q.status = status;
      if (busca) q.busca = busca;
      const [d, l] = await Promise.all([api.get('/guias/dashboard', { params: { competencia } }), api.get('/guias/profissionais', { params: q })]);
      setDash(d.data); setGrupos(l.data.grupos);
    } catch (e) { toast.error(errMsg(e, 'Erro ao carregar guias')); }
    finally { setLoading(false); }
  }, [api, competencia, categoria, status, busca]);

  useEffect(() => { load(); }, [load]);

  const openProf = (key) => setParams({ prof: key, competencia });
  const closeProf = () => setParams({ competencia });

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-5 max-w-7xl mx-auto" data-testid="guias-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><FileText className="h-7 w-7 text-primary" />Controle de Guias</h1>
            <p className="text-sm text-muted-foreground">Carteira do Kanban na data da competência × guias lançadas. Primeiro atendimento = dia seguinte à internação. Entrega até D+1.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Competência</label>
            <Input type="date" value={competencia} onChange={(e) => { setCompetencia(e.target.value); setParams(selected ? { prof: selected, competencia: e.target.value } : { competencia: e.target.value }); }} className="w-[160px]" data-testid="input-competencia" />
            <Button variant="outline" size="icon" onClick={load} data-testid="btn-atualizar"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
        </div>

        {selected ? (
          <ProfissionalPanel profKey={selected} competencia={competencia} onBack={closeProf} onChanged={load} />
        ) : (
          <>
            {dash && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                <KPI icon={Users} label="Profissionais" value={dash.profissionais} />
                <KPI icon={Users} label="Pacientes" value={dash.pacientes} />
                <KPI icon={FileText} label="Guias esperadas" value={dash.guias_esperadas} />
                <KPI icon={ClipboardCheck} label="Guias lançadas" value={dash.guias_lancadas} cls="bg-green-100 text-green-700" />
                <KPI icon={AlertTriangle} label="Pendentes" value={dash.guias_pendentes} cls="bg-red-100 text-red-700" />
                <KPI icon={PackageCheck} label="Aguardando recebimento" value={dash.aguardando_conferencia} cls="bg-yellow-100 text-yellow-700" />
                <KPI icon={CheckCircle2} label="Recebidas" value={dash.recebidas} cls="bg-emerald-100 text-emerald-700" />
                <KPI icon={AlertTriangle} label="Divergências" value={dash.divergencias} cls="bg-orange-100 text-orange-700" />
              </div>
            )}

            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input placeholder="Buscar profissional..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-8" data-testid="input-busca-prof" />
              </div>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger className="w-[190px]" data-testid="select-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {meta && Object.entries(meta.categorias).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-[210px]" data-testid="select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  {Object.entries(STATUS_STYLE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input type="checkbox" checked={ocultarVazios} onChange={(e) => setOcultarVazios(e.target.checked)} data-testid="chk-ocultar-vazios" />Ocultar sem pacientes
              </label>
            </div>

            {grupos.map((g) => {
              const itens = ocultarVazios ? g.profissionais.filter((p) => (p.pacientes_total ?? p.esperadas) > 0 || p.lancadas > 0) : g.profissionais;
              if (!itens.length) return null;
              return (
                <section key={g.categoria} className="space-y-3" data-testid={`grupo-${g.categoria}`}>
                  <h2 className="text-base md:text-lg font-semibold uppercase tracking-wide text-muted-foreground">{g.categoria_label} <span className="text-xs font-normal">({itens.length})</span></h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {itens.map((p) => <ProfCard key={p.key} p={p} onOpen={openProf} />)}
                  </div>
                </section>
              );
            })}
            {!loading && meta && !meta.is_recebedor && meta.meus_profissionais?.length === 0 && (
              <Card className="border-yellow-300 bg-yellow-50 dark:bg-yellow-950/20"><CardContent className="p-4 text-sm flex gap-2" data-testid="alerta-sem-vinculo">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                <div><b>Seu usuário ainda não está vinculado a uma rota do Kanban.</b> Peça ao administrador para vincular seu login em <i>Configurações → Equipes → Usuário do técnico</i> (ou como profissional responsável da especialidade na internação).</div>
              </CardContent></Card>
            )}
            {!loading && grupos.every((g) => (ocultarVazios ? g.profissionais.filter((p) => (p.pacientes_total ?? p.esperadas) > 0 || p.lancadas > 0) : g.profissionais).length === 0) && (
              <p className="text-center text-sm text-muted-foreground py-10">Nenhum profissional encontrado com os filtros atuais.</p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
